import { View, Platform } from 'react-native'
import Animated, { useAnimatedStyle } from 'react-native-reanimated'
import Status from '@ui/elements/Status'
import Typography from '@ui/typography'
import { statusColor } from '@ui/elements/Status/styles'
import cn from '@ui/utils/cn'
import Border from '@ui/effects/Border'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import SafePressable from '@ui/buttons/SafePressable'
import { ResultVariant } from '../types'

const AnimatedPressable = Animated.createAnimatedComponent(SafePressable)

interface Props {
  title: string
  subtitle: string
  value: number
  variant: ResultVariant
  precision?: number
  /** Makes the card a target for manual adjustment of this side. */
  onPress?: () => void
  disabled?: boolean
}

const borderRadius = 24

export default function Result({
  title,
  subtitle,
  value,
  variant,
  precision = 2,
  onPress,
  disabled,
}: Props) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  // Same press feedback as Button, so a tappable card reads as tappable in the
  // way the rest of the app already does.
  const stylePress = useAnimatedStyle(() => ({
    transform: [{ scale: 1 - animation.value * 0.025 }],
  }))

  // Stays a plain View when there is nothing to press, so a non-interactive
  // card keeps no pressed state, no scale animation and no button role.
  const Container = onPress ? AnimatedPressable : View

  return (
    <Container
      className="relative flex flex-1 flex-col"
      {...(onPress
        ? {
            onPress,
            onPressIn,
            onPressOut,
            disabled,
            accessibilityRole: 'button' as const,
            accessibilityLabel: title,
            style: stylePress,
          }
        : {})}
    >
      <Border
        style={{
          borderWidth: 2,
          borderRadius,
          borderColor: statusColor[variant].front,
        }}
      />
      <Gradient
        colors={[colors.white75, colors.white10, statusColor[variant].back]}
        style={{ borderRadius }}
        start={[0.5, 0]}
        end={[0.5, 1]}
      />
      <View
        className={cn(
          'flex flex-1 flex-col items-center justify-center',
          Platform.select({
            default: 'gap-3 px-8 py-8',
            web: 'gap-6 px-8 py-12',
          })
        )}
      >
        <Status variant={variant} text={title} />
        <Typography
          variant="XLB"
          className="pt-2 text-center font-text-semibold text-5xl text-black"
        >
          {value.toFixed(precision)}
        </Typography>
        <Typography
          variant={Platform.select({
            default: 'XSB',
            web: 'SMB',
          })}
          className="text-center font-text-semibold"
          style={{ color: statusColor[variant].front }}
        >
          {subtitle}
        </Typography>
      </View>
    </Container>
  )
}
